import React, { useState, useEffect } from 'react';
import styled from 'styled-components/macro';
import { mainTheme } from '../../styles/themes/main-theme';
import { IItemSearchResult } from '../../domain/portal-domain';
import { Spinner } from '../spinner';

const ResultsTableWrapper = styled.div`
    padding: ${mainTheme.containerMargins.small} 0;
`;

const ResultsHeader = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    padding: 0 ${mainTheme.containerMargins.small};
    padding-bottom: ${mainTheme.containerMargins.extrasmall};
    color: ${mainTheme.colors.lightGrey};
    font-size: ${mainTheme.fontSizes.small};

    @media ${mainTheme.responsiveBreakpoints.phone} {
        grid-template-columns: 1fr 1fr 1fr 1fr;
    }
`;

const ResultRow = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
    padding: ${mainTheme.margins.normal} ${mainTheme.containerMargins.small};
    font-size: ${mainTheme.fontSizes.normal};
    cursor: pointer;

    &:hover {
        background: ${mainTheme.colors.hoverBackground};
    }

    @media ${mainTheme.responsiveBreakpoints.phone} {
        grid-template-columns: 1fr 1fr 1fr 1fr;
    }
`;

const SerialNumberCell = styled.span`
    color: ${mainTheme.colors.bridgePrimary};
    min-width: 0;
    overflow-wrap: break-word;
`;

const Cell = styled.span`
    color: ${mainTheme.colors.grey};
    min-width: 0;
    overflow-wrap: break-word;
`;

const HideOnPhoneCell = styled(Cell)`
    @media ${mainTheme.responsiveBreakpoints.phone} {
        display: none;
    }
`;

const HideOnPhoneHeader = styled.span`
    @media ${mainTheme.responsiveBreakpoints.phone} {
        display: none;
    }
`;

const HighlightedText = styled.span`
    background-color: ${mainTheme.colors.hoverBackground};
`;

const LoadingWrapper = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    padding: ${mainTheme.containerMargins.big};
    color: ${mainTheme.colors.lightGrey};
    font-size: ${mainTheme.fontSizes.normal};
`;

const highlightMatch = (text: string | null | undefined, query: string): React.ReactNode => {
    if (text === null || text === undefined || text === '') return '';
    if (!query || !query.trim()) return text;

    const lowerText = text.toLowerCase();
    const lowerQuery = query.toLowerCase();
    const index = lowerText.indexOf(lowerQuery);

    if (index === -1) return text;

    const before = text.slice(0, index);
    const match = text.slice(index, index + query.length);
    const after = text.slice(index + query.length);

    return (
        <>
            {before}
            <HighlightedText>{match}</HighlightedText>
            {after}
        </>
    );
};

interface ResultsTableProps {
    results: IItemSearchResult[];
    searchQuery: string;
}

export const ResultsTable: React.FC<ResultsTableProps> = ({ results, searchQuery }) => {
    const [isNavigating, setIsNavigating] = useState(false);

    useEffect(() => {
        const handleBeforeUnload = () => {
            setIsNavigating(false);
        };

        window.addEventListener('beforeunload', handleBeforeUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            setIsNavigating(false);
        };
    }, []);

    const handleRowClick = (item: IItemSearchResult) => {
        setIsNavigating(true);
        window.location.href = `/${item.companyLegacyName}/${item.installationLegacyName}/${item.applicationId}#/items?itemId=${item.root_id}`;
    };

    if (isNavigating) {
        return (
            <LoadingWrapper>
                <Spinner text="Loading..." styles={{ size: 50, thickness: 8 }} />
            </LoadingWrapper>
        );
    }

    return (
        <ResultsTableWrapper>
            <ResultsHeader>
                <span>SN</span>
                <span>Tag</span>
                <span>Company</span>
                <span>Installation</span>
                <HideOnPhoneHeader>Certificate title</HideOnPhoneHeader>
            </ResultsHeader>
            {results.map((result, index) => (
                <ResultRow key={result.root_id ?? index} onClick={() => handleRowClick(result)}>
                    <SerialNumberCell>{highlightMatch(result.serialNumber, searchQuery)}</SerialNumberCell>
                    <Cell>{highlightMatch(result.tag, searchQuery)}</Cell>
                    <Cell>{result.companyDisplayName ?? ''}</Cell>
                    <Cell>{result.installationDisplayName ?? ''}</Cell>
                    <HideOnPhoneCell>{result.certificateTitle ?? ''}</HideOnPhoneCell>
                </ResultRow>
            ))}
        </ResultsTableWrapper>
    );
};
